import { startRegistryProcess, navigateToProcessOrTask } from '@/api/backend/workflow';
import { ServiceProviderType, RegistryActionType, RegistryActionTypeCode } from '@/api/backend/types';
import { Container } from '@/components/container';
import { PermissionGuard } from '@/components/permission-guard';
import { RegistryStatusBadge } from '@/components/registry-status-badge';
import { SubNavContainer } from '@/components/sub-nav-container';
import { PROFILE_PERMISSION } from '@/lib/constants';
import { requireContractAccess } from '@/lib/route-guards';
import { formatIsoDate } from '@/lib/utils';
import { registryItemQueryOptions } from '@/queryOptions';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Link,
  ListTable,
  ListTableCell,
  ListTableHeader,
  ListTableRow,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from '@opaz/ui-components';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceProviderDocumentsTab } from '../-components/ServiceProviderDocumentsTab';
import { ServiceProviderHistoryTab } from '../-components/ServiceProviderHistoryTab';
import { RegistryAttributesListTable } from '../../-components/RegistryAttributesListTable';
import { useBreadcrumbsStore } from '../../../../../store/breadcrumbs';

export const Route = createFileRoute('/_auth/registry/service-providers/$providerId/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    version: search.version as string | undefined,
  }),
  context: ({ params }) => ({
    getBreadcrumbData: () => ({
      id: params.providerId,
      translationKey: null,
    }),
  }),
  loader: ({ params, context }) => {
    requireContractAccess(context);
    return context.queryClient.ensureQueryData(registryItemQueryOptions(params.providerId));
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Service Provider Details - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { providerId } = useParams({ from: '/_auth/registry/service-providers/$providerId/' });
  const { version } = useSearch({ from: '/_auth/registry/service-providers/$providerId/' });
  const { updateIdToTextMap } = useBreadcrumbsStore((state) => state);

  const { data } = useSuspenseQuery(registryItemQueryOptions(providerId, version)) as { data: ServiceProviderType };

  // If the registry has no history, we show the base view (overview)
  const isRegistryBaseView = !data.history;

  const { mutateAsync: startProcess, isPending: isStartProcessLoading } = useMutation({
    mutationFn: async ({
      type,
      serviceCode,
      relatedRegistryId,
    }: {
      type: RegistryActionTypeCode;
      serviceCode: string;
      relatedRegistryId: string;
    }) => {
      const createdProcess = await startRegistryProcess({
        type,
        zoneCode: data.zoneCode,
        serviceCode,
        relatedRegistryId,
      });
      await navigateToProcessOrTask(createdProcess.id, navigate);
    },
  });

  const handleAction = (action: RegistryActionType) => {
    startProcess({
      type: action.type,
      serviceCode: action.serviceCode,
      relatedRegistryId: data.registryNumber,
    });
  };

  const handleReturnToLatest = () => {
    navigate({
      to: '/registry/service-providers/$providerId',
      params: { providerId },
      search: { version: undefined },
    });
  };

  useEffect(() => {
    updateIdToTextMap(providerId, data.registryNumber);
  }, [data.registryNumber, providerId, updateIdToTextMap]);

  return (
    <>
      <SubNavContainer>
        <div className="inline-flex flex-col gap-5">
          <div>
            <RegistryStatusBadge statusCode={data.statusCode} />
          </div>

          <h1 className="text-foreground text-3xl font-bold">
            {t([`backend.codes.${data.registryCode}` as unknown as TemplateStringsArray, 'common.unknown'])}
          </h1>
        </div>
      </SubNavContainer>
      <Container>
        {isRegistryBaseView && (
          <Alert variant="default" className="mb-4">
            <AlertTitle>{t('registry.serviceProvider.baseViewBanner.title')}</AlertTitle>
            <AlertDescription>{t('registry.serviceProvider.baseViewBanner.description')}</AlertDescription>
          </Alert>
        )}

        {version && (
          <Alert className="mb-6" variant="warning">
            <AlertTitle>{t('registry.serviceProvider.historicalVersion.title')}</AlertTitle>
            <AlertDescription>
              <span>{t('registry.serviceProvider.historicalVersion.description', { version })}</span>
              <div className="mt-4 flex justify-start">
                <Button variant="secondary" onClick={handleReturnToLatest}>
                  {t('registry.serviceProvider.historicalVersion.returnToLatest')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
          <h2 className="text-foreground text-2xl font-bold">{t('registry.serviceProvider.sectionOverview.title')}</h2>

          <PermissionGuard permission={PROFILE_PERMISSION.PMS_CONTRACT}>
            <div className="flex gap-2">
              {data.actions
                ?.filter((action) => !(action.type === 'ACT_CANCEL' && data.statusCode === 'CANCELED'))
                .map((action) => (
                  <Button
                    key={action.type}
                    variant={action.type === 'ACT_CANCEL' ? 'destructive' : 'secondary'}
                    onClick={() => handleAction(action)}
                    disabled={isStartProcessLoading}
                  >
                    {t(`registry.serviceProvider.sectionOverview.actionButtons.${action.type}`)}
                  </Button>
                ))}
            </div>
          </PermissionGuard>
        </div>
        <ListTable className="mb-12">
          <ListTableRow>
            <ListTableHeader>
              {t('registry.serviceProvider.sectionOverview.table.headings.contractNumber')}
            </ListTableHeader>

            <ListTableCell>
              {data.relatedRegistryNumber ? (
                <Link
                  onClick={() => {
                    navigate({
                      to: '/registry/contracts/$contractId',
                      params: { contractId: data.relatedRegistryNumber ?? '' },
                      search: { version: undefined },
                    });
                  }}
                >
                  {data.relatedRegistryNumber}
                </Link>
              ) : (
                '-'
              )}
            </ListTableCell>
          </ListTableRow>

          <ListTableRow>
            <ListTableHeader>{t('registry.serviceProvider.sectionOverview.table.headings.zone')}</ListTableHeader>
            <ListTableCell>{t([`common.zoneCode.${data.zoneCode}`, 'common.unknown'])}</ListTableCell>
          </ListTableRow>
          <ListTableRow>
            <ListTableHeader>{t('registry.serviceProvider.sectionOverview.table.headings.party1')}</ListTableHeader>
            <ListTableCell>{i18n.language === 'ar' ? data.party1NameAr : data.party1NameEn}</ListTableCell>
          </ListTableRow>
          <ListTableRow>
            <ListTableHeader>{t('registry.serviceProvider.sectionOverview.table.headings.party2')}</ListTableHeader>
            <ListTableCell>{i18n.language === 'ar' ? data.party2NameAr : data.party2NameEn}</ListTableCell>
          </ListTableRow>

          <ListTableRow>
            <ListTableHeader>
              {t('registry.serviceProvider.sectionOverview.table.headings.providerCRNumber')}
            </ListTableHeader>
            <ListTableCell>{data.party2}</ListTableCell>
          </ListTableRow>

          <ListTableRow>
            <ListTableHeader>{t('registry.serviceProvider.sectionOverview.table.headings.startDate')}</ListTableHeader>
            <ListTableCell>{formatIsoDate(data.startDate, i18n.language)}</ListTableCell>
          </ListTableRow>
        </ListTable>

        {!isRegistryBaseView && (
          <>
            <RegistryAttributesListTable
              title={t('registry.serviceProvider.sectionAttributes.title')}
              attributes={data.attributes}
            />

            {/* Tabs for History and Documents */}
            <TabsRoot defaultValue="history">
              <TabsList className="mb-6">
                <TabsTrigger value="history">{t('registry.contract.sectionTabs.historyTab.title')}</TabsTrigger>
                <TabsTrigger value="documents">{t('registry.contract.sectionTabs.documentsTab.title')}</TabsTrigger>
              </TabsList>

              <TabsContent value="history">
                <ServiceProviderHistoryTab data={data} />
              </TabsContent>

              <TabsContent value="documents">
                <ServiceProviderDocumentsTab data={data} />
              </TabsContent>
            </TabsRoot>
          </>
        )}
      </Container>
    </>
  );
}
